import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Lock, Sparkles, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "dimbani_paywall_phone";

/**
 * PredictionPaywall — wraps prediction cards with a blur+lock overlay
 * until the visitor purchases a weekly subscription. Payment is
 * currently MOCKED (returns immediate success) so we can wire the
 * front-end flow now and swap in Stripe / M-Pesa later without any UI
 * changes.
 *
 * Usage:
 *   <PredictionPaywall>
 *     <PredictionsList ... />
 *   </PredictionPaywall>
 */
const PredictionPaywall = ({ children, matchCount = 0 }) => {
    const savedPhone = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) || "" : "";
    const [phone, setPhone] = useState(savedPhone);
    const [status, setStatus] = useState({ loading: true, unlocked: false, sub: null });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState("weekly-basic");
    const [dialogPhone, setDialogPhone] = useState(savedPhone);
    const [buying, setBuying] = useState(false);

    const checkStatus = useCallback(async (p) => {
        if (!p) { setStatus({ loading: false, unlocked: false, sub: null }); return; }
        try {
            const r = await axios.get(`${API}/paywall/status`, { params: { phone: p } });
            setStatus({ loading: false, unlocked: !!r.data.unlocked, sub: r.data.subscription || null });
        } catch {
            setStatus({ loading: false, unlocked: false, sub: null });
        }
    }, []);

    useEffect(() => {
        checkStatus(phone);
        axios.get(`${API}/paywall/plans`).then((r) => setPlans(r.data?.plans || [])).catch(() => {});
    }, [phone, checkStatus]);

    const purchase = async () => {
        if (!dialogPhone || dialogPhone.trim().length < 9) {
            toast.error("Weka namba ya simu (mfano +255712000000)");
            return;
        }
        setBuying(true);
        try {
            const r = await axios.post(`${API}/paywall/subscribe`, {
                phone: dialogPhone.trim(),
                plan_id: selectedPlan,
                payment_method: "mock",
            });
            window.localStorage.setItem(STORAGE_KEY, dialogPhone.trim());
            setPhone(dialogPhone.trim());
            toast.success("Umefungua utabiri!", { description: `Muda: ${r.data.subscription.plan_label}` });
            setDialogOpen(false);
            await checkStatus(dialogPhone.trim());
        } catch (e) {
            toast.error("Malipo yameshindwa", { description: e?.response?.data?.detail || e.message });
        } finally {
            setBuying(false);
        }
    };

    if (status.loading) {
        return (
            <div className="flex items-center justify-center py-8 text-white/40" data-testid="paywall-loading">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Inakagua ufunguo…
            </div>
        );
    }

    return (
        <div className="relative" data-testid="prediction-paywall">
            {/* Content — blurred when locked */}
            <div className={status.unlocked ? "" : "pointer-events-none select-none blur-md opacity-70"}>
                {children}
            </div>

            {/* Overlay CTA */}
            {!status.unlocked && (
                <Card className="absolute inset-x-0 top-8 mx-auto max-w-md p-6 bg-gradient-to-br from-electric/20 to-paper/95 border-electric/50 backdrop-blur-sm" data-testid="paywall-overlay">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-electric/20 flex items-center justify-center">
                            <Lock className="h-6 w-6 text-electric" />
                        </div>
                        <div>
                            <h3 className="font-heading text-lg font-black text-white">Utabiri Umefungwa</h3>
                            <p className="text-xs text-white/60">Lipia ili kuona utabiri wa mechi {matchCount ? `zote ${matchCount}` : "hizi"}.</p>
                        </div>
                    </div>
                    <div className="space-y-1.5 mb-4 text-xs text-white/80">
                        <div className="flex items-center gap-2"><Sparkles className="h-3 w-3 text-electric" /> Utabiri wa AI mchanganyiko</div>
                        <div className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-300" /> Kikosi kamili + takwimu</div>
                        <div className="flex items-center gap-2"><ShieldCheck className="h-3 w-3 text-yellow-300" /> Malipo salama · M-Pesa / Airtel / Kadi</div>
                    </div>
                    <Button
                        onClick={() => { setDialogPhone(phone); setDialogOpen(true); }}
                        className="w-full bg-electric text-white hover:bg-electric/90 font-bold"
                        data-testid="paywall-cta"
                    >
                        <Lock className="h-4 w-4 mr-2" /> Fungua kwa TZS 5,000
                    </Button>
                    <p className="text-[10px] text-white/40 text-center mt-2">Wiki 1 · TZS 5,000 · Ghairi wakati wowote</p>
                </Card>
            )}

            {/* Unlocked banner */}
            {status.unlocked && status.sub && (
                <div className="mb-4 flex items-center gap-2 p-3 rounded border border-emerald-400/40 bg-emerald-400/[0.06]" data-testid="paywall-unlocked-banner">
                    <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    <span className="text-sm text-white">
                        Umefungua utabiri · Inaisha {new Date(status.sub.expires_at).toLocaleDateString("sw-TZ")}
                    </span>
                </div>
            )}

            {/* Subscribe dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-paper border-electric/50 max-w-md" data-testid="paywall-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Lock className="h-5 w-5 text-electric" /> Fungua Utabiri
                        </DialogTitle>
                        <DialogDescription className="text-xs text-white/50">
                            Chagua kifurushi na uweke namba yako ya simu. Utapata utabiri wote wa AI mara moja.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {plans.map((p) => {
                            const on = selectedPlan === p.id;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedPlan(p.id)}
                                    data-testid={`plan-${p.id}`}
                                    className={`w-full text-left p-3 rounded border transition-colors ${
                                        on ? "border-electric bg-electric/10" : "border-border bg-obsidian hover:border-electric/40"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-heading font-bold text-white">{p.label}</div>
                                            <div className="text-[11px] text-white/50">{p.days} siku</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-heading font-black text-electric tabular-nums">TZS {p.amount.toLocaleString()}</div>
                                            {p.id === "monthly-basic" && <Badge className="text-[9px] bg-yellow-300/20 text-yellow-300 border-yellow-300/40 border">Punguzo</Badge>}
                                        </div>
                                    </div>
                                    <ul className="mt-2 text-[11px] text-white/60 space-y-0.5">
                                        {p.features.map((f) => <li key={f}>✓ {f}</li>)}
                                    </ul>
                                </button>
                            );
                        })}
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-white/50">Namba ya simu</label>
                            <Input
                                value={dialogPhone}
                                onChange={(e) => setDialogPhone(e.target.value)}
                                placeholder="+255712000000"
                                className="bg-obsidian border-border text-white font-mono"
                                data-testid="paywall-phone-input"
                            />
                        </div>
                        <p className="text-[10px] text-white/40 italic">
                            🧪 Malipo yamewekwa MOCK kwa sasa — utafunguliwa mara moja bila kulipia. Stripe / M-Pesa itaunganishwa hivi karibuni.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border text-white">Ghairi</Button>
                        <Button onClick={purchase} disabled={buying} className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold" data-testid="paywall-purchase">
                            {buying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                            Fungua Sasa
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PredictionPaywall;
